
import { VueRecaptcha } from "vue-recaptcha";
import useVuelidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { lcService } from "@/helpers/api-services/lc.service";
import { servicesService } from "@/helpers/api-services/services.service";
import { authComputed } from "@/state/helpers";
import { profileComputed } from "@/state/helpers";
import { GoogleTrackingService } from "@/helpers/tracking-services/google-tracking-service";
import Share from "@/components/share-button-group.vue";
import { contentService } from "@/helpers/api-services/content.service";
import i18n from "@/i18n";
import { defineComponent, reactive } from "vue";
import { KcmContent, LeadCapturePostData } from "@/types";
declare interface ComponentData {
  currentType: string;
  phone: string;
  sitekey: string;
  reCaptcha: boolean;
  reCapError: boolean;
  guide: KcmContent | undefined;
}

export default defineComponent({
  components: { VueRecaptcha, Share },

  setup() {
    const state = reactive({
      name: "",
      email: "",
    });
    const validations = {
      name: { required },
      email: { required, email },
    };
    return { state, v$: useVuelidate(validations, state) };
  },
  data(): ComponentData {
    return {
      currentType: "",
      phone: "",
      sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY || "",
      reCaptcha: false,
      reCapError: false,
      guide: undefined,
    };
  },

  props: {
    type: {
      type: String,
      required: true,
    },
    page: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    checkType(currentType: string) {
      return this.currentType === currentType;
    },

    checkTypeIncludes(currentType: string) {
      return this.currentType.includes(currentType);
    },

    checkLanguage(currentLanguage: string) {
      return i18n.locale === currentLanguage;
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async verifyMethod(response: any): Promise<void> {
      this.reCaptcha = true;
      this.reCapError = false;
      await servicesService
        .postRecaptchaResponse({ site: "stm", client_response: response })
        .then((result) => {
          console.log(result);
        })
        .catch((error) => alert(error.message));
    },

    expiredMethod(): void {
      this.reCaptcha = false;
    },

    async getGuide(): Promise<void> {
      this.v$.name.$touch();
      this.v$.email.$touch();
      if (!this.v$.name.$invalid && !this.v$.email.$invalid && this.reCaptcha) {
        await lcService
          .postLead(this.lcData)
          .then((result) => {
            if (result.error) {
              console.error(result.error);
              alert(result.error);
            }
          })
          .catch((error) => {
            console.error(error);
            alert(error.message);
          })
          .finally(() => {
            if (this.page) {
              this.currentType = this.currentType + "ty";
            } else {
              const path = "/" + i18n.locale + "/" + this.currentType + "ty/";
              const query = this.$route.query;
              this.$router.push({
                path: path,
                query: query,
              });
            }
          });
      } else if (!this.v$.$invalid && !this.reCaptcha) {
        this.reCapError = true;
      }
    },

    // Maybe type this one day.
    setUpRecaptcha(): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const reCaptcha = document.querySelector('[title="reCAPTCHA"]') as any;
      if (reCaptcha) {
        reCaptcha.title = reCaptcha.title + this.type;
        reCaptcha.src = reCaptcha.src.replace(/(&hl=e.)/, "&hl=" + i18n.locale);
      }
    },

    gtagFocusBlur: GoogleTrackingService.gtagFocusBlur,
    localeToString: contentService.localeToString,

    async getGuideCover(): Promise<void> {
      let category = this.currentType;
      if (this.checkTypeIncludes("ty")) {
        category = category.split("ty")[0];
      }
      if (category === "fthb") {
        category = "first-time-home-buyers";
      }
      await contentService
        .getContents(0, 1, "guide", i18n.locale, category)
        .then((resp) => {
          if (resp.content) {
            this.guide = resp.content[0];
          }
        });
    },
  },

  mounted() {
    this.currentType = this.type;
    this.getGuideCover();
    if (this.page) {
      GoogleTrackingService.modifyGoogleTagManager("guide");
      if (this.checkType("buyers")) {
        document.title =
          this.$t("lead.headBuy") + " | " + this.title1 + " " + this.title2;
      } else if (this.checkType("sellers")) {
        document.title =
          this.$t("lead.headSell") + " | " + this.title1 + " " + this.title2;
      } else if (this.checkType("fthb")) {
        document.title =
          this.$t("lead.headFTHB") + " | " + this.title1 + " " + this.title2;
      } else if (this.checkType("buyersty")) {
        document.title =
          this.$t("lead.headBuyTY") + " | " + this.title1 + " " + this.title2;
      } else if (this.checkType("sellersty")) {
        document.title =
          this.$t("lead.headSellTY") + " | " + this.title1 + " " + this.title2;
      } else if (this.checkType("fthbty")) {
        document.title =
          this.$t("lead.headFTHBTY") + " | " + this.title1 + " " + this.title2;
      }
    }
  },

  computed: {
    ...authComputed,
    ...profileComputed,

    getOpenImageGuideLocation() {
      if (this.guide && this.guide.assets) {
        const asset = this.guide.assets.find(
          (asset) => asset.title === "Open Image"
        );
        return asset ? asset.url : "";
      }
      return "";
    },

    getCoverImageGuideLocation() {
      if (this.guide && this.guide.assets) {
        const asset = this.guide.assets.find(
          (asset) => asset.title === "Cover Image"
        );
        return asset ? asset.url : "";
      }
      return "";
    },

    imageAlt() {
      if (this.checkTypeIncludes("buyer")) {
        return "Buyer's Guide";
      } else if (this.checkTypeIncludes("seller")) {
        return "Seller's Guide";
      } else {
        return "FTHB Guide";
      }
    },

    typeMinusTy(): string {
      return this.currentType.replace(/ty/g, "");
    },

    guideLink() {
      const mapToPP: { [key: string]: string } = {
        buyers: "buyer",
        sellers: "seller",
        fthb: "fthb",
      };

      const currentGuideData =
        this.profilePP.guides[
          mapToPP[this.typeMinusTy] +
            (i18n.locale === "es" ? "sp" : "") +
            "_link"
        ];

      // get the members guide link from profile API, if it exists
      if (currentGuideData) {
        return currentGuideData.value;
      }

      // otherwise, fallback to link building
      if (this.checkTypeIncludes("buyer")) {
        return (
          "https://www.mykcm.com/scripts/guide/?g=buyer" +
          (i18n.locale === "es" ? "sp" : "") +
          "&contactId=" +
          this.authUserID
        );
      } else if (this.checkTypeIncludes("seller")) {
        return (
          "https://www.mykcm.com/scripts/guide/?g=seller" +
          (i18n.locale === "es" ? "sp" : "") +
          "&contactId=" +
          this.authUserID
        );
      } else {
        return (
          "https://www.mykcm.com/scripts/guide/?g=fthb" +
          (i18n.locale === "es" ? "sp" : "") +
          "&contactId=" +
          this.authUserID
        );
      }
    },

    lcData(): LeadCapturePostData {
      return {
        auth_id: this.authUserID,
        member_email:
          this.profilePP &&
          this.profilePP.blog &&
          this.profilePP.blog.lead_email &&
          this.profilePP.blog.lead_email.value
            ? this.profilePP.blog.lead_email.value
            : "",
        name: this.state.name,
        lead_email: this.state.email,
        phone: this.phone,
        send_guide: {
          type: this.guideType,
          title: this.guideTitle,
          link: this.guideLink,
          member_subject: this.guideSubject,
        },
        page_viewed: window.location.href,
        profile: {
          profile_full_name:
            this.profilePP &&
            this.profilePP.basic &&
            this.profilePP.basic.full_name &&
            this.profilePP.basic.full_name.value
              ? this.profilePP.basic.full_name.value
              : "",
          profile_company:
            this.profilePP &&
            this.profilePP.basic &&
            this.profilePP.basic.company &&
            this.profilePP.basic.company.value
              ? this.profilePP.basic.company.value
              : "",
          profile_phone:
            this.profilePP &&
            this.profilePP.basic &&
            this.profilePP.basic.phone &&
            this.profilePP.basic.phone.value
              ? this.profilePP.basic.phone.value
              : "",
          profile_from:
            this.title1 +
            " " +
            (this.profilePP &&
            this.profilePP.blog &&
            this.profilePP.blog.pos_A &&
            this.profilePP.blog.pos_A.value
              ? this.profilePP.blog.pos_A.value
              : "Keeping Current Matters"),
        },
      };
    },

    guideTitle(): string {
      if (this.checkTypeIncludes("buyer")) {
        return this.$t("lead.headBuy") as string;
      } else if (this.checkTypeIncludes("seller")) {
        return this.$t("lead.headSell") as string;
      } else {
        return this.$t("lead.headFTHB") as string;
      }
    },

    guideSubject(): string {
      if (this.checkTypeIncludes("buyer")) {
        return "Buyer Guide Request";
      } else if (this.checkTypeIncludes("seller")) {
        return "Seller Guide Request";
      } else {
        return "First Time Home Buyer Guide Request";
      }
    },

    guideType() {
      if (this.checkTypeIncludes("buyer")) {
        return i18n.locale === "es" ? "buyersp" : "buyer";
      } else if (this.checkTypeIncludes("seller")) {
        return i18n.locale === "es" ? "sellersp" : "seller";
      } else {
        return i18n.locale === "es" ? "fthbsp" : "fthb";
      }
    },
    dynamicInternationalizationLookup(): string {
      return `lead.sidebarGuideText.${this.type}`;
    },
  },
});
